import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api";
import { OrgInvitation } from "../../../generated/core-api";
import { useAuthenticated } from "../../../authn";

/**
 * Hook for managing server-based invitations (fetching and responding)
 */
export function useServerInvitation() {
  const auth = useAuthenticated();
  const queryClient = useQueryClient();

  // Fetch server invitations
  const query = useQuery({
    queryKey: ["selfInvitations", auth.userId],
    queryFn: () => api.listInvitationsBySelf(),
  });

  // Respond to server invitation
  const respondToServerInvitation = async (
    invitation: OrgInvitation,
    accept: boolean
  ): Promise<boolean> => {
    try {
      await api.respondToInvitationById({
        org_id: invitation.org_id,
        category: invitation.org_category,
        invitation_id: invitation.invitation_id,
        accept,
      });

      // Invalidate server invitations query to refresh data
      queryClient.invalidateQueries({ queryKey: ["selfInvitations"] });
      return true;
    } catch (error) {
      console.error("Failed to respond to server invitation:", error);
      return false;
    }
  };

  return {
    invitations: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    respondToServerInvitation,
    // Expose query for additional operations if needed
    query,
  };
}
